import {
  AfterViewInit,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { CardVariant } from '@design-system/components/m-card';
import { SummaryMetricService } from '@portal/app/dashboard/context-modal/services/summary-metric.service';
import { ReturnCurveChartService } from '@portal/app/dashboard/context-modal/services/return-curve-chart.service';
import { Column } from '@portal/app/dashboard/context-modal/components/driver-display/driver-display.component';
import { Options } from 'highcharts';
import { CurrencyPipe } from '@angular/common';
import { SPEND_MODEL, ViewBy } from '@portal/app/shared/constants';
import { LinkService } from '@portal/app/dashboard/context-modal/services/link.service';
import { ModalService } from '@portal/app/dashboard/context-modal/services/modal.service';

@Component({
  selector: 'portal-context-tactic-summary',
  templateUrl: './tactic-summary.component.html',
  styleUrl: './tactic-summary.component.scss',
  providers: [SummaryMetricService, ReturnCurveChartService]
})
export class TacticSummaryComponent implements AfterViewInit {
  public cardVariant = CardVariant;
  public columnType = Column;
  private summaryMetricService = inject(SummaryMetricService);
  private returnCurveService = inject(ReturnCurveChartService);
  private currencyPipe = inject(CurrencyPipe);
  private linkService = inject(LinkService);
  private modalService = inject(ModalService);

  comparisonPeriod = input.required<string>();

  isLoading = this.summaryMetricService.isLoading;
  isDataAvailable = this.summaryMetricService.isDataAvailable;
  topMetric = this.summaryMetricService.topMetric;
  drivers = this.summaryMetricService.drivers;
  isSpendLevel = this.modalService.isSpendLevel;

  returnCurveIsLoading = this.returnCurveService.isLoading;
  returnCurveChartOptions = this.returnCurveService.chartOptions;
  returnCurveIsReady = this.returnCurveService.isChartReady;
  returnCurveValid = this.returnCurveService.isChartValid;
  selectedMetricType = signal<'salesI' | 'ordersI'>('ordersI');

  ctModelInfo = this.linkService.ctModelInfo;

  // Store reference to chart instance for redrawing
  private chartInstance: Highcharts.Chart | null = null;
  viewInitialized = false;

  // Dropdown options for metric selection using proper constants
  metricOptions = [
    { label: 'Incremental Orders', value: 'ordersI' },
    { label: 'Incremental Sales', value: 'salesI' }
  ];

  returnCurveTooltip =
    this.ctModelInfo() === SPEND_MODEL
      ? '<div class="w-67 pl-2 pr-2 text-center">Return curve uses latest model results,</br> up to 3 years weekly media spends</div>'
      : '<div class="w-67 pl-2 pr-2 text-center">Return curve uses latest model results,</br> and adjustment factors are drawn from </br> your Cross-Channel Dashboard.</div>';

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.viewInitialized = true;
    });
  }

  // Chart options computed signal with real data from API
  chartOptions = computed(() => {
    const returnCurveResponse = this.returnCurveService.data().response;
    if (
      !returnCurveResponse ||
      !returnCurveResponse.data ||
      returnCurveResponse.data.length === 0
    ) {
      // Return base chart config while loading or if no data
      return {};
    }

    // Determine the effective viewBy mode based on selected metric type
    // This ensures the chart shows the correct data type (sales vs orders)
    const effectiveViewBy =
      this.selectedMetricType() === 'salesI' ? ViewBy.roas : ViewBy.cpo;

    return this.returnCurveService.getChartOptions(
      returnCurveResponse,
      effectiveViewBy,
      this.currencyPipe
    );
  });

  onMetricTypeChange(metricType: 'salesI' | 'ordersI'): void {
    this.selectedMetricType.set(metricType);
    // Redraw the chart when metric type changes
    this.redrawChart();
  }

  // Handle chart render event to store chart instance
  onChartRender(chart: Highcharts.Chart): void {
    this.chartInstance = chart;
  }

  // Redraw the chart with new options
  private redrawChart(): void {
    if (!this.chartInstance) {
      return;
    }

    const options = this.chartOptions();
    if (!options) {
      return;
    }

    try {
      this.chartInstance.update(options as Options, true);
    } catch (error) {
      // Silently handle chart update errors in production
    }
  }
}
